import React, { useState, useEffect, useRef } from "react";
import { Formik } from "formik";
import * as yup from "yup";
import { webhookType, webhookTypeName } from "../../../../../../constants";
import Modal from "../../../../../components/modal";
import TextInput from "../../../../../components/form/text-input";
import Select from "../../../../../components/form/select";
import Button from "../../../../../components/new-button";
import formBaseStyles from "./../../../../../components/form/text-input/styles.module.scss";
import layoutStyles from "../../../../../styles/layout.module.scss";
import FancyRadioInput from "../../../../../components/form/fancy-radio-input";
import Alert from "../../../../../components/alert";
import styles from "./styles.module.scss";
import TagInput from "../../../../../components/form/tag-input";
import Progression from "./components/tag-input-dropdown";
import CopyIcon from "../../../../../assets/svg/icons/common/copy";
import ArrowsClockwise from "../../../../../assets/svg/icons/common/arrows-clockwise";
import Plus from "../../../../../assets/svg/icons/common/Plus";
import ButtonIcon from '../../../../../components/ButtonIcon/ButtonIcon'
import CrossInCircle from "../../../../../assets/svg/icons/common/CrossInCircle";
import { GetOrganizationById, saveWebhook, generateWebhookSecret } from "../../../../../../api";
import useOrganizationStore from "../../../../../stores/organization";
import toast from "react-hot-toast";
import { arrayToObject } from "../../../../../../components/helper/helper";
import ConfirmationModal from "../../../../application/components/ConfirmationModal/ConfirmationModal";

// Surface why the server rejected a webhook. SafeWebhookUrl answers 422 with a
// Laravel bag ({errors: {url: ["...must use https..."]}}), the permission check
// answers 403 with {message}, and api.js reshapes a non-JSON body to {error}.
// Toasting a flat "Failed to save webhook" for all three left an admin holding a
// URL the form refused with no way to find out what was wrong with it.
const webhookErrorMessage = (err, fallback) => {
  const bag = err?.data?.errors;
  const first = bag && Object.values(bag).find(Boolean);
  if (first) return Array.isArray(first) ? first[0] : first;
  return err?.data?.message ?? err?.error ?? fallback;
};

const schema = yup.object({
  type: yup.string().trim().required("* Type is required"),
  url: yup.string().url('* URL is invalid').trim().required("* URL is required"),
  secret: yup.string().trim().notRequired(),
  method: yup.string().oneOf(["GET", "POST", "PUT"], "* Method required").required("* Method required"),
  headers: yup.array().of(yup.object({
    header_name: yup.string().trim().notRequired(),
    header_value: yup.string().trim().notRequired(),
  })),
  body: yup.array().of(yup.object({
    body_name: yup.string().trim().notRequired(),
    body_value: yup.string().trim().notRequired(),
  })),
  progressions: yup.array().of(yup.number()).required("* Progressions are required"),
});

const AddEditWebhookModal = ({ open, onClose, isEdit = false }) => {

  const selectedOrganizationId = useOrganizationStore((state) => state.selectedOrganizationId);
  const formikRef = useRef(null);

  const [errors, setErrors] = useState({});
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [formData, setFormData] = useState({
    type: "",
    url: "",
    secret: "Random Secret",
    method: "",
    progressions: [],
    version: 2,
    headers: [{
      header_name: "",
      header_value: "",
    }],
    body: [{
      body_name: "",
      body_value: "",
    }],
  });
  const [progressionOptions, setProgressionOptions] = useState([]);
  const [statusVersions, setStatusVersions] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const [showSecret, setShowSecret] = useState(false);

  const transformWebhookPayload = (values) => {
    const headerKeys = {};
    const headerValues = {};
    const bodyKeys = {};
    const bodyValues = {};

    values.headers.forEach((h, index) => {
      headerKeys[`headerKey${index}`] = h.header_name;
      headerValues[`headerValue${index}`] = h.header_value;
    });

    values.body.forEach((b, index) => {
      bodyKeys[`bodyKey${index}`] = b.body_name;
      bodyValues[`bodyValue${index}`] = b.body_value;
    });

    return {
      url: values.url,
      method: values.method,
      headerKeys,
      headerValues,
      bodyKeys,
      bodyValues,
      type: values.type,
      organisationId: selectedOrganizationId,
      progressions: values.progressions,
      version: values.version,
    };
  };

  useEffect(() => {
    GetOrganizationById(selectedOrganizationId).then((response) => {
      let options = Object.entries(response.progressions).map(
        ([key, value]) => {
          return { label: value, value: key };
        },
      );
      setProgressionOptions(options);
      setStatusVersions(response.statusVersions || []);

      const webhooks = response.organization.webhooks;
      setWebhooks(webhooks);
    });
  }, []);

  useEffect(() => {
  }, [formData]);

  const handleMemberFormSubmit = async (values) => {
    const toastId = toast.loading('Saving webhook...', { duration: 10000 });
    try {
      if (!values.method) {
        toast.error('Method is required', { id: toastId, duration: 5000 });
        return;
      }

      if (formikRef.current) {
        formikRef.current.validateForm().then(errors => {
          if (Object.keys(errors).length > 0) {
            toast.error('Please fix validation errors', { id: toastId, duration: 5000 });
            return;
          }

          saveWebhook(transformWebhookPayload(values)).then(res => {
            if (res.status) {
              toast.success('Webhook saved successfully', { id: toastId, duration: 5000 });
              onClose();
            } else {
              toast.error('Failed to save webhook', { id: toastId, duration: 5000 });
            }
          }).catch(err => {
            toast.error(webhookErrorMessage(err, 'Failed to save webhook'), { id: toastId, duration: 8000 });
          });
        });
      } else {
        const res = await saveWebhook(transformWebhookPayload(values));
        if (res.status) {
          toast.success('Webhook saved successfully', { id: toastId, duration: 5000 });
          onClose();
        } else {
          toast.error('Failed to save webhook', { id: toastId, duration: 5000 });
        }
      }
    } catch (err) {
      toast.error(webhookErrorMessage(err, 'Failed to save webhook'), { id: toastId, duration: 8000 });
    }
  };

  const setMethodValue = (method) => {
    if (formikRef.current) {
      formikRef.current.setFieldValue('method', method, true);
      formikRef.current.validateForm();
    }
  };

  const onChangeType = (value) => {
    const webhook = webhooks.find((webhook) => webhook.type === value);
    setSelectedStatuses(JSON.parse(webhook.progression_to_update));

    let method = webhook.call_type || "";
    if (method && !["GET", "POST", "PUT"].includes(method)) {
      method = "GET";
    }

    let headersArray = [];
    try {
      const headersObj = JSON.parse(webhook.headers);
      headersArray = Object.entries(headersObj).map(([key, value]) => ({
        header_name: key,
        // A blank row is saved as "": null; the form's fields are strings.
        header_value: value ?? ""
      }));
    } catch (error) {
      headersArray = [];
    }

    // covert body from object format to array format
    let bodyArray = [];
    try {
      const bodyObj = JSON.parse(webhook.body);
      bodyArray = Object.entries(bodyObj).map(([key, value]) => ({
        body_name: key,
        body_value: value ?? ""
      }));
    } catch (error) {
      bodyArray = [];
    }

    const updatedFormData = {
      ...formData,
      url: webhook.url || "",
      method: method,
      type: webhook.type,
      progressions: JSON.parse(webhook.progression_to_update),
      version: webhook.version,
      headers: headersArray.length > 0 ? headersArray : formData.headers,
      body: bodyArray.length > 0 ? bodyArray : formData.body,
    };

    setFormData(updatedFormData);

    if (method) {
      setTimeout(() => {
        setMethodValue(method);
      }, 0);
    }

    if (formikRef.current && webhook.url) {
      setTimeout(() => {
        formikRef.current.setFieldValue('url', webhook.url, true);
      }, 0);
    }

    if (formikRef.current && webhook.progression_to_update) {
      setTimeout(() => {
        formikRef.current.setFieldValue('progressions', JSON.parse(webhook.progression_to_update), true);
        formikRef.current.setFieldValue('version', webhook.version, true);
      }, 0);
    }

    if (formikRef.current && headersArray.length > 0) {
      setTimeout(() => {
        formikRef.current.setFieldValue('headers', headersArray, true);
      }, 0);
    }

    if (formikRef.current && bodyArray.length > 0) {
      setTimeout(() => {
        formikRef.current.setFieldValue('body', bodyArray, true);
      }, 0);
    }

    setShowSecret(false);
  };

  const generateSecret = async () => {
    if (!selectedOrganizationId) {
      toast.error('Organization ID is missing', { duration: 5000 });
      setShowConfirmationModal(false);
      return;
    }
    
    if (!formData.type) {
      toast.error('Webhook type is required to generate a secret', { duration: 5000 });
      setShowConfirmationModal(false);
      return;
    }

    const toastId = toast.loading('Generating secret...', { duration: 10000 });
    
    try {
      const res = await generateWebhookSecret({ 
        organizationId: selectedOrganizationId, 
        type: formData.type 
      });
      
      if (res.status) {
        setFormData({
          ...formData,
          secret: res.secret,
        });
        
        if (formikRef.current) {
          formikRef.current.setFieldValue('secret', res.secret, true);
        }
        
        setShowSecret(true);
        setShowConfirmationModal(false);
        toast.success('Secret generated successfully', { id: toastId, duration: 5000 });
      } else {
        setShowConfirmationModal(false);
        toast.error(res.message || 'Failed to generate secret', { id: toastId, duration: 5000 });
      }
    } catch (error) {
      setShowConfirmationModal(false);
      toast.error(
        error.message || 'An unexpected error occurred while generating the secret',
        { id: toastId, duration: 5000 }
      );
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(formData.secret);
    toast.success('Secret copied to clipboard', { duration: 5000 });
  };


  return (
    <>
      <Modal
        isOpen={open}
        onClose={onClose}
        header={`Add/Edit Webhook`}
        notCentered
        size="md"
        withAlert
      >
        <div className={styles.alertContainer}>
          <Alert type="warning">
            Caution! This functionality is for developers only.
          </Alert>
        </div>
        <Formik
          innerRef={formikRef}
          enableReinitialize={false}
          validationSchema={schema}
          onSubmit={handleMemberFormSubmit}
          initialValues={formData}
          initialErrors={errors}
          validateOnBlur={true}
          validateOnChange={true}
        >
          {({
            handleSubmit,
            handleChange,
            values,
            touched,
            errors,
            setFieldValue,
            isSubmitting,
          }) => {
            if (values.method && errors.method) {
              setTimeout(() => {
                setFieldValue('method', values.method, true);
              }, 0);
            }

            return (
              <form autoComplete="off" onSubmit={handleSubmit}>
                <div className={`${layoutStyles.grid} ${layoutStyles.gap2}`}>
                  <div
                    className={`${layoutStyles.grid} ${layoutStyles.gridCols2} ${layoutStyles.gap2}`}
                  >
                    <Select
                      label="Type"
                      placeholder="Select type"
                      id="type"
                      name="type"
                      options={Object.keys(webhookTypeName).map((key) => ({
                        value: key,
                        label: webhookTypeName[key],
                      }))}
                      value={values?.type}
                      onChange={(value) => {
                        setFieldValue("type", value);
                        onChangeType(value);
                        setFormData({
                          ...formData,
                          type: value,
                        });
                      }}
                      errored={errors.type}
                      error={errors["type"]}
                    />
                    <TextInput
                      label="URL"
                      id="url"
                      name="url"
                      type="url"
                      value={values.url}
                      onChange={handleChange}
                      touched={touched.url}
                      errored={errors.url}
                      error={errors["url"]}
                    />
                  </div>

                  <div
                    className={`${layoutStyles.grid} ${layoutStyles.gridCols1} ${layoutStyles.gap2}`}
                  >
                    <div>
                      <label htmlFor="secret" className={styles.label}>Secret</label>
                      <div className={styles.secretContainer} >
                        <input
                          type={showSecret ? "text" : "password"}
                          value={values.secret}
                          className={styles.secretInput}
                          onChange={(e) => {
                            setFieldValue("secret", e.target.value);
                          }}
                          readOnly={false}
                        />
                        {showSecret ? (
                          <button
                            className={styles.secretButton}
                            onClick={copySecret}
                            type="button"
                            title="Copy secret"
                          >
                            <CopyIcon color="#fff" />
                          </button>
                        ) : (
                          <button
                            className={styles.secretButton}
                            onClick={() => {
                              setShowConfirmationModal(true);
                            }}
                            title="Generate secret"
                            type="button"
                          >
                            <ArrowsClockwise />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={styles.headersContainer} >
                    <div className={`${layoutStyles.flex} ${layoutStyles.flexSpaceBetween} ${layoutStyles.alignCenter}`} >
                      <p className={styles.fieldTitle} >Headers</p>
                      <ButtonIcon
                        bgColor={'#4C70EF'}
                        textColor="#fff"
                        size="small"
                        onClick={() => {
                          setFieldValue("headers", [...values.headers, { header_name: "", header_value: "" }]);
                        }}
                        type="button"
                      >
                        <Plus />
                      </ButtonIcon>
                    </div>
                    {values.headers.map((header, index) => (
                      <div className={`${layoutStyles.grid} ${layoutStyles.gridCols2} ${layoutStyles.gap2}`} >
                        <TextInput
                          label="Header Name"
                          id="header_name"
                          name="header_name"
                          value={header.header_name}
                          onChange={(e) => {
                            const updatedHeaders = [...values.headers];
                            updatedHeaders[index].header_name = e.target.value;
                            setFieldValue("headers", updatedHeaders);
                          }}
                          touched={touched.headers?.[index]?.header_name}
                          errored={errors?.headers?.[index]?.header_name}
                          error={errors?.headers?.[index]?.header_name}
                        />
                        <TextInput
                          label="Header Value"
                          id="header_value"
                          name="header_value"
                          value={header.header_value}
                          onChange={(e) => {
                            const updatedHeaders = [...values.headers];
                            updatedHeaders[index].header_value = e.target.value;
                            setFieldValue("headers", updatedHeaders);
                          }}
                          touched={touched.headers?.[index]?.header_value}
                          errored={errors?.headers?.[index]?.header_value}
                          error={errors?.headers?.[index]?.header_value}
                          suffix={<ButtonIcon
                            bgColor={'#fff'}
                            size="small"
                            onClick={() => {
                              setFieldValue("headers", values.headers.filter((_, i) => i !== index));
                            }}
                          >
                            <CrossInCircle />
                          </ButtonIcon>}
                        />
                      </div>
                    ))}
                  </div>


                  <div className={styles.headersContainer} >
                    <div className={`${layoutStyles.flex} ${layoutStyles.flexSpaceBetween} ${layoutStyles.alignCenter}`} >
                      <p className={styles.fieldTitle} >Body</p>
                      <ButtonIcon
                        bgColor={'#4C70EF'}
                        textColor="#fff"
                        size="small"
                        onClick={() => {
                          setFieldValue("body", [...values.body, { body_name: "", body_value: "" }]);
                        }}
                        type="button"
                      >
                        <Plus />
                      </ButtonIcon>
                    </div>
                    {values.body.map((body, index) => (
                      <div className={`${layoutStyles.grid} ${layoutStyles.gridCols2} ${layoutStyles.gap2}`} >
                        <TextInput
                          label="Body Name"
                          id="body_name"
                          name="body_name"
                          value={body.body_name}
                          onChange={(e) => {
                            const updatedBody = [...values.body];
                            updatedBody[index].body_name = e.target.value;
                            setFieldValue("body", updatedBody);
                          }}
                          touched={touched.body?.[index]?.body_name}
                          errored={errors?.body?.[index]?.body_name}
                          error={errors?.body?.[index]?.body_name}
                        />
                        <TextInput
                          label="Body Value"
                          id="body_value"
                          name="body_value"
                          value={body.body_value}
                          onChange={(e) => {
                            const updatedBody = [...values.body];
                            updatedBody[index].body_value = e.target.value;
                            setFieldValue("body", updatedBody);
                          }}
                          touched={touched.body?.[index]?.body_value}
                          errored={errors?.body?.[index]?.body_value}
                          error={errors?.body?.[index]?.body_value}
                          suffix={<ButtonIcon
                            bgColor={'#fff'}
                            size="small"
                            onClick={() => {
                              setFieldValue("body", values.body.filter((_, i) => i !== index));
                            }}
                          >
                            <CrossInCircle />
                          </ButtonIcon>}
                        />
                      </div>
                    ))}
                  </div>

                </div>

                <div className={`${layoutStyles.mt2}`} >
                  <label
                    className={`${formBaseStyles.label} ${formBaseStyles.labelLarge}`}
                  >
                    Method
                  </label>
                  <div
                    className={`${layoutStyles.flex} ${layoutStyles.flexWrap} ${layoutStyles.gap1}`}
                  >
                    <FancyRadioInput
                      id="method_get"
                      name="method"
                      label="GET"
                      value="GET"
                      checked={values.method === "GET"}
                      onChange={() => {
                        setMethodValue("GET");
                      }}
                    />
                    <FancyRadioInput
                      id="method_post"
                      name="method"
                      label="POST"
                      value="POST"
                      checked={values.method === "POST"}
                      onChange={() => {
                        setMethodValue("POST");
                      }}
                    />
                    <FancyRadioInput
                      id="method_put"
                      name="method"
                      label="PUT"
                      value="PUT"
                      checked={values.method === "PUT"}
                      onChange={() => {
                        setMethodValue("PUT");
                      }}
                    />
                  </div>
                  {errors.method && <p className={styles.error}>{errors.method}</p>}
                </div>

                {values.type == webhookType.PAYMENT_CLOUD && (
                  <div
                    className={`${layoutStyles.grid} ${layoutStyles.gridCols1} ${layoutStyles.gap2} ${layoutStyles.mt2}`}
                  >
                    <Select
                      label="Webhook version"
                      id="version"
                      name="version"
                      options={statusVersions.map(({ version, label }) => ({ value: version, label }))}
                      value={values.version}
                      hideClearButton
                      onChange={(version) => {
                        if (!version) return;
                        // Select what the subscription covers in that version.
                        const saved = webhooks.find((webhook) => webhook.type === values.type);
                        setFieldValue("version", version);
                        setFieldValue("progressions", saved?.progressions_by_version?.[version] || []);
                      }}
                    />
                    <Progression
                      selectedStatuses={values.progressions}
                      options={statusVersions.length
                        ? Object.entries(statusVersions.find((v) => v.version === values.version)?.progressions || {})
                          .map(([value, label]) => ({ label, value }))
                        : progressionOptions}
                      onChange={(value) => {
                        setFieldValue("progressions", value);
                      }}
                    />
                    {errors.progressions && <p className={styles.error}>{errors.progressions}</p>}
                  </div>
                )}

                <div className={`${layoutStyles.grid} ${layoutStyles.gap2} ${layoutStyles.mt1}`}>
                  <div
                    className={`${layoutStyles.flex} ${layoutStyles.flexEnd} ${layoutStyles.gap1}`}
                  >
                    <Button type="button" variant="ghost" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                      Save
                    </Button>
                  </div>
                </div>
              </form>
            );
          }}
        </Formik>
      </Modal>

      {showConfirmationModal && (
        <ConfirmationModal
          open={showConfirmationModal}
          onClose={() => setShowConfirmationModal(false)}
          message="Are you sure? Generating a new secret will render the previous one invalid for future requests."
          onConfirmClick={() => {
            generateSecret();
          }}
        />
      )}
    </>
  );
};

export default AddEditWebhookModal;